import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import {
  File,
  FileText,
  Image as ImageIcon,
  FileArchive,
  Video,
  Music,
  Loader2,
  Check,
  Search,
  FileX
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

// Get file icon based on type
const getFileIcon = (fileType) => {
  if (!fileType) return File;
  const type = fileType.toLowerCase();
  if (type.startsWith('image') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(type)) return ImageIcon;
  if (type.startsWith('video') || ['mp4', 'mov', 'avi', 'webm'].includes(type)) return Video;
  if (type.startsWith('audio') || ['mp3', 'wav', 'ogg'].includes(type)) return Music;
  if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(type)) return FileText;
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(type)) return FileArchive;
  return File;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Dialog for selecting a document from the project's uploaded files
 * to link to a compliance requirement.
 * Fetches from project_files table directly (matching ComplianceExplorer behavior).
 */
export function LinkDocumentDialog({
  open,
  onOpenChange,
  projectId,
  onSelect,
  title = 'Link Document',
  description = 'Select a document from your uploaded files to link to this requirement.'
}) {
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load files from project_files table
  const loadFiles = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('project_files')
        .select(`
          id,
          name,
          file_path,
          file_size,
          file_type,
          uploaded_by,
          uploaded_date,
          user_profiles!uploaded_by(first_name, last_name, avatar)
        `)
        .eq('project_id', projectId)
        .order('uploaded_date', { ascending: false });

      if (error) throw error;

      setFiles(data || []);
    } catch (err) {
      console.error('Error loading files:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Load data when dialog opens
  useEffect(() => {
    if (open && projectId) {
      setSelectedFile(null);
      setSearchQuery('');
      loadFiles();
    }
  }, [open, projectId, loadFiles]);

  // Filter files by search query
  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle file selection
  const handleSelect = () => {
    if (selectedFile && onSelect) {
      onSelect({
        id: selectedFile.id,
        name: selectedFile.name,
        url: selectedFile.file_path,
        file_path: selectedFile.file_path,
        file_type: selectedFile.file_type
      });
      onOpenChange(false);
    }
  };

  // Get uploader name
  const getUploaderName = (file) => {
    if (!file.user_profiles) return null;
    return `${file.user_profiles.first_name || ''} ${file.user_profiles.last_name || ''}`.trim();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <p className="text-sm text-muted-foreground">{description}</p>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* File browser */}
        <ScrollArea className="flex-1 min-h-[300px] border rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center h-full py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-gray-500">
              <FileX className="h-12 w-12 text-gray-300 mb-3" />
              <p className="text-sm font-medium">
                {searchQuery ? 'No files match your search' : 'No files uploaded yet'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {searchQuery
                  ? 'Try a different search term'
                  : 'Upload files from the Docs page first'}
              </p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {filteredFiles.map((file) => {
                const FileIcon = getFileIcon(file.file_type);
                const isSelected = selectedFile?.id === file.id;
                const uploaderName = getUploaderName(file);

                return (
                  <button
                    key={file.id}
                    onClick={() => setSelectedFile(file)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left",
                      isSelected
                        ? "bg-violet-100 border-2 border-violet-500"
                        : "hover:bg-gray-100 border-2 border-transparent"
                    )}
                  >
                    <FileIcon className={cn("h-5 w-5 flex-shrink-0", isSelected ? "text-violet-600" : "text-gray-400")} />
                    <div className="flex-1 min-w-0">
                      <p className={cn("text-sm truncate", isSelected ? "font-medium text-violet-900" : "text-gray-900")}>
                        {file.name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{formatFileSize(file.file_size)}</span>
                        {file.file_type && (
                          <>
                            <span>•</span>
                            <span className="uppercase">{file.file_type}</span>
                          </>
                        )}
                        {file.uploaded_date && (
                          <>
                            <span>•</span>
                            <span>{formatDistanceToNow(new Date(file.uploaded_date), { addSuffix: true })}</span>
                          </>
                        )}
                        {uploaderName && (
                          <>
                            <span>•</span>
                            <span className="truncate max-w-[100px]">by {uploaderName}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-violet-600 flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* File count */}
        {!loading && files.length > 0 && (
          <p className="text-xs text-gray-500">
            {filteredFiles.length} of {files.length} file{files.length !== 1 ? 's' : ''}
            {searchQuery && ' matching your search'}
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSelect}
            disabled={!selectedFile}
            className="bg-violet-600 hover:bg-violet-700"
          >
            Link Document
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
